import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { listSupplierDirectory, bulkUpdateSuppliers, fetchHqCountries, fetchCountries, createSupplierInfo } from '../services/supplierService';
import { fetchUserDirectory } from '../services/graphService';
import SupplierInfoModal from '../components/modals/SupplierInfoModal';
import MultiSelect from '../components/MultiSelect';
import useUser from '../auth/useUser';
import { useMsal } from '@azure/msal-react';
import './Suppliers.css';
import './SuppliersList.css';

const DEFAULT_PAGE_SIZE = 25;

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'green';
  if (s === 'pending') return 'amber';
  if (s === 'suspended') return 'red';
  return 'neutral';
}

function SortHead({ label, column, sort, onSort }) {
  const active = sort.column === column;
  const icon = active ? (sort.direction === 'asc' ? '↑' : '↓') : '';
  return (
    <th className="sup-sort-head" onClick={() => onSort(column)}>
      {label}
      {active && <span className="sup-sort-ico">{icon}</span>}
    </th>
  );
}

/* ── Graph-backed person picker (same UX as SupplierInfoModal) ── */
function PersonPicker({ label, value, onChange, users, loadingUsers }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('pp-portal-drop');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.slice(0, 30);
    return users
      .filter(u =>
        u.displayName.toLowerCase().includes(q) ||
        u.userPrincipalName.toLowerCase().includes(q) ||
        (u.mail && u.mail.toLowerCase().includes(q))
      )
      .slice(0, 30);
  }, [users, query]);

  const dropdownEl = open && dropPos && createPortal(
    <div
      id="pp-portal-drop"
      className="ms-drop ms-drop-portal"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 220, overflowY: 'auto' }}
    >
      <div
        className="ms-opt"
        style={{ color: 'var(--t3)', fontStyle: 'italic' }}
        onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
      >
        <span className="ms-opt-lbl">✕ Clear</span>
      </div>
      {filtered.map(u => (
        <div
          key={u.id}
          className="ms-opt"
          onClick={() => { onChange(u.displayName); setOpen(false); setQuery(''); }}
        >
          <span className="ms-opt-lbl">
            <strong>{u.displayName}</strong>
            {u.mail && <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 6 }}>{u.mail}</span>}
          </span>
        </div>
      ))}
      {!loadingUsers && filtered.length === 0 && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--t3)' }}>No matches</div>
      )}
      {loadingUsers && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--t3)' }}>Loading directory…</div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef}>
      <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>{label}</div>
      <input
        className="sup-input"
        value={open ? query : (value || '')}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(value || ''); }}
        placeholder={loadingUsers ? 'Loading users…' : 'Start typing…'}
        style={{ padding: '5px 10px', fontSize: 12.5 }}
      />
      {dropdownEl}
    </div>
  );
}

/* ── Country picker for bulk edit ── */
function CountryPicker({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);
  const [dropPos, setDropPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        const portal = document.getElementById('cp-portal-drop');
        if (portal && portal.contains(e.target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 30);
    return options.filter(c => c.toLowerCase().includes(q)).slice(0, 30);
  }, [options, query]);

  const dropdownEl = open && dropPos && createPortal(
    <div
      id="cp-portal-drop"
      className="ms-drop ms-drop-portal"
      style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width, maxHeight: 220, overflowY: 'auto' }}
    >
      <div
        className="ms-opt"
        style={{ color: 'var(--t3)', fontStyle: 'italic' }}
        onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
      >
        <span className="ms-opt-lbl">✕ Clear</span>
      </div>
      {filtered.map(c => (
        <div
          key={c}
          className="ms-opt"
          onClick={() => { onChange(c); setOpen(false); setQuery(''); }}
        >
          <span className="ms-opt-lbl">{c}</span>
        </div>
      ))}
      {filtered.length === 0 && (
        <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--t3)' }}>No matches</div>
      )}
    </div>,
    document.body
  );

  return (
    <div ref={wrapRef}>
      <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>{label}</div>
      <input
        className="sup-input"
        value={open ? query : (value || '')}
        onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(value || ''); }}
        placeholder="Start typing…"
        style={{ padding: '5px 10px', fontSize: 12.5 }}
      />
      {dropdownEl}
    </div>
  );
}

/* ── Add New Supplier Modal ── */
const STATUS_OPTIONS = ['Pending', 'Approved', 'Suspended'];
const EMPTY_CONTACT = () => ({ contact: '', title: '', country: '', email: '', phone: '' });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddNewSupplierModal({ onClose, onSaved, onToast, editorName, msalInstance }) {
  const [form, setForm] = useState({
    supplier_name: '',
    supplier_approval_status: 'Pending',
    supplier_headquarter_country: '',
    account_manager: '',
    account_manager2: '',
    supplier_url: '',
    supplier_newsroom: '',
  });
  const [contacts, setContacts] = useState([EMPTY_CONTACT()]);
  const [saving, setSaving] = useState(false);
  const [dupError, setDupError] = useState(null);
  const [emailErrors, setEmailErrors] = useState({});
  const [emptyConfirm, setEmptyConfirm] = useState(null);
  const [countries, setCountries] = useState([]);
  const [graphUsers, setGraphUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const usersLoadedRef = useRef(false);
  const contactRefs = useRef([]);

  useEffect(() => { fetchCountries().then(setCountries).catch(() => {}); }, []);
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const loadUsers = () => {
    if (usersLoadedRef.current) return;
    usersLoadedRef.current = true;
    setLoadingUsers(true);
    fetchUserDirectory(msalInstance)
      .then(u => setGraphUsers(u || []))
      .finally(() => setLoadingUsers(false));
  };

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (k === 'supplier_name') setDupError(null); };
  const setContact = (idx, k, v) => {
    setContacts(prev => prev.map((c, i) => i === idx ? { ...c, [k]: v } : c));
    if (k === 'email') setEmailErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
  };
  const addContact = () => setContacts(prev => [...prev, EMPTY_CONTACT()]);
  const removeContact = (idx) => setContacts(prev => prev.filter((_, i) => i !== idx));

  const isContactEmpty = (c) => !c.contact.trim() && !c.title.trim() && !c.country.trim() && !c.email.trim() && !c.phone.trim();

  const doSave = async (skipEmptyCheck) => {
    // Validate supplier name
    if (!form.supplier_name.trim()) {
      onToast({ type: 'error', msg: 'Supplier Name is required.' });
      return;
    }

    // Validate emails
    const badEmails = {};
    contacts.forEach((c, i) => {
      if (c.email.trim() && !EMAIL_RE.test(c.email.trim())) badEmails[i] = true;
    });
    if (Object.keys(badEmails).length > 0) {
      setEmailErrors(badEmails);
      const first = Number(Object.keys(badEmails)[0]);
      onToast({ type: 'error', msg: `Contact #${first + 1}: Contact Email is invalid. Please fix it before saving.` });
      if (contactRefs.current[first]) contactRefs.current[first].scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Check for empty blocks
    if (!skipEmptyCheck) {
      const emptyCount = contacts.filter(isContactEmpty).length;
      if (emptyCount > 0 && emptyCount < contacts.length) {
        setEmptyConfirm(emptyCount);
        return;
      }
    }

    setSaving(true);
    try {
      const validContacts = contacts.filter(c => !isContactEmpty(c));
      const payload = {
        ...form,
        contacts: validContacts,
      };
      const result = await createSupplierInfo(payload, editorName);
      onToast({ type: 'ok', msg: `Supplier '${form.supplier_name.trim()}' added successfully (ID: ${result.supplier_id || result.id})` });
      if (onSaved) onSaved(result);
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.error === 'DUPLICATE') {
        setDupError(data.message);
      } else {
        onToast({ type: 'error', msg: data?.error || 'Failed to add supplier' });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose}>
      <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 800 }}>
        <div className="sup-modal-head">
          <h5 className="sup-modal-title mb-0">Add New Supplier</h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="sup-modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {/* ── Section 1: Supplier Information ── */}
          <div className="row g-3">
            {/* Row 1 */}
            <div className="col-12 col-md-4">
              <div className="sup-field-label">Supplier Name <span style={{ color: '#dc2626' }}>*</span></div>
              <input
                className="sup-input"
                value={form.supplier_name}
                onChange={(e) => set('supplier_name', e.target.value)}
                placeholder="e.g. Adalvo Ltd"
                style={dupError ? { borderColor: '#dc2626' } : undefined}
              />
              {dupError && (
                <div style={{ fontSize: 12, color: '#dc2626', marginTop: 4 }}>{dupError}</div>
              )}
            </div>
            <div className="col-12 col-md-4">
              <div className="sup-field-label">Supplier Status</div>
              <select className="sup-select" value={form.supplier_approval_status} onChange={(e) => set('supplier_approval_status', e.target.value)}>
                {STATUS_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className="col-12 col-md-4">
              <CountryPicker
                label="Supplier Head Quarter Country"
                value={form.supplier_headquarter_country}
                onChange={(v) => set('supplier_headquarter_country', v)}
                options={countries}
              />
            </div>

            {/* Row 2: person pickers */}
            <div className="col-12 col-md-6" onFocus={loadUsers}>
              <PersonPicker
                label="Internal FocusCare Person"
                value={form.account_manager}
                onChange={(v) => set('account_manager', v)}
                users={graphUsers}
                loadingUsers={loadingUsers}
              />
            </div>
            <div className="col-12 col-md-6" onFocus={loadUsers}>
              <PersonPicker
                label="Backup Account Manager"
                value={form.account_manager2}
                onChange={(v) => set('account_manager2', v)}
                users={graphUsers}
                loadingUsers={loadingUsers}
              />
            </div>
            {/* Row 3: URL + Newsroom */}
            <div className="col-12 col-md-6">
              <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Supplier Web Site</div>
              <input className="sup-input" value={form.supplier_url} onChange={(e) => set('supplier_url', e.target.value)} placeholder="https://example.com" style={{ fontSize: 12.5 }} />
            </div>
            <div className="col-12 col-md-6">
              <div className="sup-field-label" style={{ marginBottom: 2, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.03em', fontWeight: 600, color: 'var(--navy)' }}>Supplier Newsroom</div>
              <input className="sup-input" value={form.supplier_newsroom} onChange={(e) => set('supplier_newsroom', e.target.value)} placeholder="https://example.com/news" style={{ fontSize: 12.5 }} />
            </div>
          </div>

          {/* ── Section 2: Contacts ── */}
          <div style={{ background: '#f0f7f0', borderRadius: 8, padding: '14px 16px', marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Contacts</div>
              <button
                type="button"
                onClick={addContact}
                title="Add another contact"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--teal)', fontSize: 18, lineHeight: 1, padding: 0 }}
              >
                <i className="bi bi-plus-circle-fill"></i>
              </button>
            </div>

            {contacts.map((c, idx) => (
              <div
                key={idx}
                ref={el => { contactRefs.current[idx] = el; }}
                style={{ background: '#fff', borderRadius: 6, padding: '10px 12px', marginBottom: idx < contacts.length - 1 ? 10 : 0, border: '1px solid #e5e7eb' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--navy)', textTransform: 'uppercase' }}>Contact #{idx + 1}</span>
                  {contacts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContact(idx)}
                      title="Remove this contact"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 14, padding: 0, lineHeight: 1 }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  )}
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <div className="sup-field-label" style={{ fontSize: 11 }}>Contact</div>
                    <input className="sup-input" placeholder="Full name" value={c.contact} onChange={(e) => setContact(idx, 'contact', e.target.value)} style={{ fontSize: 12.5 }} />
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="sup-field-label" style={{ fontSize: 11 }}>Title</div>
                    <input className="sup-input" placeholder="Job title" value={c.title} onChange={(e) => setContact(idx, 'title', e.target.value)} style={{ fontSize: 12.5 }} />
                  </div>
                  <div className="col-12 col-md-4">
                    <CountryPicker
                      label="Location"
                      value={c.country}
                      onChange={(v) => setContact(idx, 'country', v)}
                      options={countries}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="sup-field-label" style={{ fontSize: 11 }}>Contact Email</div>
                    <input
                      className="sup-input"
                      type="email"
                      placeholder="name@example.com"
                      value={c.email}
                      onChange={(e) => setContact(idx, 'email', e.target.value)}
                      onBlur={() => {
                        if (c.email.trim() && !EMAIL_RE.test(c.email.trim())) {
                          setEmailErrors(prev => ({ ...prev, [idx]: true }));
                        }
                      }}
                      style={{ fontSize: 12.5, ...(emailErrors[idx] ? { borderColor: '#dc2626' } : {}) }}
                    />
                    {emailErrors[idx] && (
                      <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>Invalid email</div>
                    )}
                  </div>
                  <div className="col-12 col-md-6">
                    <div className="sup-field-label" style={{ fontSize: 11 }}>Contact Phone</div>
                    <input className="sup-input" placeholder="+1 555-0100" value={c.phone} onChange={(e) => setContact(idx, 'phone', e.target.value)} style={{ fontSize: 12.5 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button type="button" className="sup-btn primary" onClick={() => doSave(false)} disabled={saving}>
            <i className="bi bi-check-circle me-1"></i> {saving ? 'Creating...' : 'Create Supplier'}
          </button>
        </div>
      </div>

      {/* Empty blocks confirmation */}
      {emptyConfirm != null && (
        <div className="sup-modal-backdrop" style={{ zIndex: 1080 }} onClick={() => setEmptyConfirm(null)}>
          <div className="sup-modal" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h5 className="sup-modal-title mb-0">Empty contacts</h5>
              <button type="button" className="sup-modal-close" onClick={() => setEmptyConfirm(null)}>&times;</button>
            </div>
            <div className="sup-modal-body" style={{ fontSize: 14 }}>
              You have {emptyConfirm} empty contact block{emptyConfirm > 1 ? 's' : ''}. Empty blocks will be skipped. Continue?
            </div>
            <div className="sup-modal-foot">
              <button type="button" className="sup-btn" onClick={() => setEmptyConfirm(null)}>Go Back</button>
              <button type="button" className="sup-btn primary" onClick={() => { setEmptyConfirm(null); doSave(true); }}>Save Anyway</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Bulk Edit Suppliers Modal ── */
function BulkEditSuppliersModal({ count, selectedIds, onClose, onSaved, onToast, editorName, msalInstance }) {
  const [accountManager, setAccountManager] = useState('');
  const [hqCountry, setHqCountry] = useState('');
  const [saving, setSaving] = useState(false);
  const [graphUsers, setGraphUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [hqOptions, setHqOptions] = useState([]);
  const usersLoadedRef = useRef(false);

  useEffect(() => {
    fetchHqCountries().then(setHqOptions).catch(() => {});
  }, []);

  const loadUsers = () => {
    if (usersLoadedRef.current) return;
    usersLoadedRef.current = true;
    setLoadingUsers(true);
    fetchUserDirectory(msalInstance)
      .then(u => setGraphUsers(u || []))
      .finally(() => setLoadingUsers(false));
  };

  const apply = async () => {
    const updates = {};
    if (accountManager) updates.account_manager = accountManager;
    if (hqCountry) updates.supplier_headquarter_country = hqCountry;
    if (Object.keys(updates).length === 0) {
      onToast({ type: 'error', msg: 'Select at least one field to update' });
      return;
    }
    setSaving(true);
    try {
      const res = await bulkUpdateSuppliers(selectedIds, updates, editorName);
      onToast({ type: 'ok', msg: `Updated ${res.updated_count || selectedIds.length} suppliers.` });
      onSaved();
      onClose();
    } catch {
      onToast({ type: 'error', msg: 'Bulk update failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sup-modal-backdrop" onClick={onClose} style={{ zIndex: 1070 }}>
      <div className="sup-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="sup-modal-head">
          <div>
            <h5 className="sup-modal-title mb-0">Bulk Edit Suppliers</h5>
            <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 2 }}>Updating {count} suppliers</div>
          </div>
          <button type="button" className="sup-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="sup-modal-body">
          <div style={{ marginBottom: 16 }} onFocus={loadUsers}>
            <PersonPicker
              label="Internal FocusCare Person"
              value={accountManager}
              onChange={setAccountManager}
              users={graphUsers}
              loadingUsers={loadingUsers}
            />
          </div>
          <div>
            <CountryPicker
              label="HQ Country"
              value={hqCountry}
              onChange={setHqCountry}
              options={hqOptions}
            />
          </div>
        </div>
        <div className="sup-modal-foot">
          <button type="button" className="sup-btn" onClick={onClose} disabled={saving}>
            <i className="bi bi-x-circle me-1"></i> Cancel
          </button>
          <button type="button" className="sup-btn primary" onClick={apply} disabled={saving}>
            <i className="bi bi-check-circle me-1"></i> {saving ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function SuppliersList() {
  const { displayName: editorName } = useUser();
  const { instance: msalInstance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ column: 'supplier_name', direction: 'asc' });
  const [openRow, setOpenRow] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [hqFilter, setHqFilter] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [favorites, setFavorites] = useState(() => new Set());

  // Fetch user's favorite suppliers (authenticated)
  useEffect(() => {
    const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';
    (async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return;
        let token;
        try { token = (await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] })).accessToken; }
        catch { token = (await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] })).accessToken; }
        const r = await fetch(`${import.meta.env.VITE_API_URL}/api/favorite-suppliers`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setFavorites(new Set(d.suppliers || []));
        }
      } catch {}
    })();
  }, [msalInstance]);

  const toggleFavorite = useCallback(async (supplierName) => {
    const API_SCOPE = 'api://6069b616-c7f2-4978-9f9c-180ec12f1844/access_as_user';
    const isFav = favorites.has(supplierName);
    // Optimistic update
    setFavorites(prev => {
      const next = new Set(prev);
      if (isFav) next.delete(supplierName); else next.add(supplierName);
      return next;
    });
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return;
      let token;
      try { token = (await msalInstance.acquireTokenSilent({ scopes: [API_SCOPE], account: accounts[0] })).accessToken; }
      catch { token = (await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] })).accessToken; }
      if (isFav) {
        await fetch(`${import.meta.env.VITE_API_URL}/api/favorite-suppliers/${encodeURIComponent(supplierName)}`, {
          method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await fetch(`${import.meta.env.VITE_API_URL}/api/favorite-suppliers`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ supplierName }),
        });
      }
    } catch {
      // Revert on error
      setFavorites(prev => {
        const next = new Set(prev);
        if (isFav) next.add(supplierName); else next.delete(supplierName);
        return next;
      });
    }
  }, [favorites, msalInstance]);

  // URL params as source of truth for filter values
  const pushFiltersToUrl = useCallback((s, hq) => {
    const p = new URLSearchParams();
    if (s) p.set('search', s);
    if (hq && hq.length > 0) p.set('hq_country', hq.join(','));
    const qs = p.toString();
    navigate(qs ? `/suppliers-list?${qs}` : '/suppliers-list', { replace: true });
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.toString() === '') return; // bare URL → keep current state
    setSearch(params.get('search') || '');
    const hq = params.get('hq_country');
    setHqFilter(hq ? hq.split(',') : []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const load = useCallback(async () => {
    setRows(null); setErr('');
    try {
      const data = await listSupplierDirectory();
      setRows(data);
    } catch {
      setErr('Failed to load supplier directory.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Nova-driven auto-open of a supplier modal
  useEffect(() => {
    if (!rows) return;
    const name = sessionStorage.getItem('novaOpenSupplier');
    if (!name) return;
    const match = rows.find(r => String(r.supplier_name || '').toLowerCase() === name.toLowerCase())
                || rows.find(r => String(r.supplier_name || '').toLowerCase().includes(name.toLowerCase()));
    if (match) setOpenRow(match);
    sessionStorage.removeItem('novaOpenSupplier');
  }, [rows]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Reset selection on filter/page change
  useEffect(() => { setPage(1); setSelected(new Set()); }, [search, sort, hqFilter]);
  useEffect(() => { setSelected(new Set()); }, [page]);

  const hqCountryOptions = useMemo(() => {
    if (!rows) return [];
    return [...new Set(rows.map(s => s.supplier_headquarter_country).filter(Boolean))].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let arr = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      arr = arr.filter(r =>
        String(r.supplier_name || '').toLowerCase().includes(q) ||
        String(r.account_manager || '').toLowerCase().includes(q)
      );
    }
    if (hqFilter.length > 0) {
      arr = arr.filter(r => hqFilter.includes(r.supplier_headquarter_country));
    }
    return arr;
  }, [rows, search, hqFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.direction === 'desc' ? -1 : 1;
    arr.sort((a, b) => {
      const av = String(a[sort.column] ?? '').toLowerCase();
      const bv = String(b[sort.column] ?? '').toLowerCase();
      if (av === bv) return 0;
      return av < bv ? -dir : dir;
    });
    return arr;
  }, [filtered, sort]);

  const effectivePageSize = Math.max(1, pageSize || DEFAULT_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(sorted.length / effectivePageSize));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * effectivePageSize;
  const pageEnd = Math.min(pageStart + effectivePageSize, sorted.length);
  const pageRows = sorted.slice(pageStart, pageEnd);

  const handleSort = (column) => {
    setSort(s => (s.column === column
      ? { column, direction: s.direction === 'asc' ? 'desc' : 'asc' }
      : { column, direction: 'asc' }));
  };

  // Checkbox helpers
  const allPageChecked = pageRows.length > 0 && pageRows.every(r => selected.has(r.supplier_id));
  const somePageChecked = pageRows.some(r => selected.has(r.supplier_id));
  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allPageChecked) {
        pageRows.forEach(r => next.delete(r.supplier_id));
      } else {
        pageRows.forEach(r => next.add(r.supplier_id));
      }
      return next;
    });
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="sup-page">
      <div className="d-flex align-items-start justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="sup-heading mb-1">Suppliers List</h2>
          <div className="sup-breadcrumb">FocusOne &middot; Supplier Data &middot; Suppliers List</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selected.size > 0 && (
            <button
              type="button"
              className="sup-btn primary"
              onClick={() => setBulkOpen(true)}
            >
              Edit Selected ({selected.size})
            </button>
          )}
          <button
            type="button"
            className="sup-btn primary"
            onClick={() => setCreateOpen(true)}
          >
            <i className="bi bi-plus-circle me-1"></i> Add New Supplier
          </button>
        </div>
      </div>

      {err && <div className="sup-error-box mb-3">{err}</div>}

      <div className="sl-toolbar" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <div className="sl-search">
          <i className="bi bi-search"></i>
          <input
            type="text"
            placeholder="Search by supplier name or account manager…"
            value={search}
            onChange={(e) => { const v = e.target.value; setSearch(v); pushFiltersToUrl(v, hqFilter); }}
          />
          {search && (
            <button
              type="button"
              className="sl-search-clear"
              onClick={() => { setSearch(''); pushFiltersToUrl('', hqFilter); }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <MultiSelect
          label="HQ Country"
          options={hqCountryOptions}
          values={hqFilter}
          onChange={(v) => { setHqFilter(v); pushFiltersToUrl(search, v); }}
          placeholder="All Countries"
          minWidth={180}
        />
      </div>

      {!rows && !err && (
        <div className="sup-table-wrap p-4">
          {[0, 1, 2, 3, 4].map(i => (
            <div key={i} className="sup-skeleton mb-2" style={{ height: 28, width: '100%' }} />
          ))}
        </div>
      )}

      {rows && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll">
            <table className="sup-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={allPageChecked}
                      ref={(el) => { if (el) el.indeterminate = somePageChecked && !allPageChecked; }}
                      onChange={toggleAll}
                      aria-label="Select all on page"
                    />
                  </th>
                  <th style={{ width: 40 }}>Open</th>
                  <th style={{ width: 36 }} title="Favorite">Fav</th>
                  <SortHead label="Supplier Name"      column="supplier_name"               sort={sort} onSort={handleSort} />
                  <SortHead label="Supplier Status"    column="supplier_approval_status"    sort={sort} onSort={handleSort} />
                  <SortHead label="Head Quarter Country" column="supplier_headquarter_country" sort={sort} onSort={handleSort} />
                  <SortHead label="Internal FocusCare Person"    column="account_manager"             sort={sort} onSort={handleSort} />
                  <th style={{ minWidth: 150 }}>Name</th>
                  <th style={{ minWidth: 150 }}>Title</th>
                  <th style={{ minWidth: 120 }}>Phone</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.supplier_id} className={selected.has(r.supplier_id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(r.supplier_id)}
                        onChange={() => toggleOne(r.supplier_id)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="sl-open-btn"
                        title="Open supplier info"
                        aria-label="Open supplier info"
                        onClick={() => setOpenRow(r)}
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        title={favorites.has(r.supplier_name) ? 'Remove from favorites' : 'Add to favorites'}
                        onClick={() => toggleFavorite(r.supplier_name)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, padding: 0, lineHeight: 1, color: favorites.has(r.supplier_name) ? '#f59e0b' : '#d1d5db' }}
                      >
                        {favorites.has(r.supplier_name) ? '\u2605' : '\u2606'}
                      </button>
                    </td>
                    <td><span className="sup-prod-name">{r.supplier_name || '—'}</span></td>
                    <td>
                      <span className={`sup-badge ${statusBadgeClass(r.supplier_approval_status)}`}>
                        {r.supplier_approval_status || '—'}
                      </span>
                    </td>
                    <td>{r.supplier_headquarter_country || '—'}</td>
                    <td>{r.account_manager || '—'}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.full_name || '').split(/,\s*/)[0] || '—'}</td>
                    <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.title_list || '').split(/,\s*/)[0] || '—'}</td>
                    <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(r.phone_list || '').split(/,\s*/)[0] || '—'}</td>
                  </tr>
                ))}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: 30, color: 'var(--t3)' }}>
                      No suppliers found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
            <button
              type="button"
              onClick={() => { if (pageSize < sorted.length) { setPageSize(sorted.length); } else { setPageSize(DEFAULT_PAGE_SIZE); } setPage(1); }}
              style={{ fontSize: 12, color: 'var(--teal)', border: '1px solid var(--teal)', background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {pageSize < sorted.length ? 'Show All' : `Show ${DEFAULT_PAGE_SIZE}`}
            </button>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
              {(() => {
                const win = 5;
                let s = Math.max(1, safePage - Math.floor(win / 2));
                let e = Math.min(pageCount, s + win - 1);
                s = Math.max(1, e - win + 1);
                const nums = [];
                for (let i = s; i <= e; i++) nums.push(i);
                return nums.map(n => (
                  <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
                ));
              })()}
              <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {sorted.length === 0 ? 0 : pageStart + 1}–{pageEnd} of {sorted.length.toLocaleString('en-US')} {sorted.length === 1 ? 'supplier' : 'suppliers'}
            </span>
          </div>
        </div>
      )}

      {openRow && (
        <SupplierInfoModal
          supplierId={openRow.supplier_id}
          supplierName={openRow.supplier_name}
          onClose={() => setOpenRow(null)}
          onSaved={() => load()}
          onToast={setToast}
        />
      )}

      {createOpen && (
        <AddNewSupplierModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => load()}
          onToast={setToast}
          editorName={editorName}
          msalInstance={msalInstance}
        />
      )}

      {bulkOpen && (
        <BulkEditSuppliersModal
          count={selected.size}
          selectedIds={[...selected]}
          onClose={() => setBulkOpen(false)}
          onSaved={() => { load(); setSelected(new Set()); }}
          onToast={setToast}
          editorName={editorName}
          msalInstance={msalInstance}
        />
      )}

      {toast && (
        <div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
